// packages/web-ui/src/panels/views/EdgeLegend.tsx
//
// Small overlay rendered on top of the React Flow viewport that explains the
// per-kind connector styles defined in `ConnectionEdge.EDGE_KIND_STYLES`.
// Renders only the kinds actually present in the current edge set (avoids
// noise on views that only use one flow type).

import type { Edge } from '@xyflow/react';
import type { CSSProperties, JSX } from 'react';
import {
  EDGE_KIND_STYLES,
  KIND_ICON,
  type EdgeKindStyle,
} from './ConnectionEdge.js';
import type { ConnectionKind, ConnectionEdgeData } from './layout-builder.js';

const LEGEND_ORDER: ConnectionKind[] = [
  'energy',
  'mechanical',
  'information',
  'material',
  'reference',
];

export interface EdgeLegendProps {
  edges: ReadonlyArray<Edge>;
  /** Absolute placement inside the React Flow viewport wrapper. Default `bottom-right`. */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
}

function presentKinds(edges: ReadonlyArray<Edge>): ConnectionKind[] {
  const set = new Set<ConnectionKind>();
  for (const e of edges) {
    const data = e.data as ConnectionEdgeData | undefined;
    if (data?.kind !== undefined && data.kind in EDGE_KIND_STYLES) {
      set.add(data.kind);
    }
  }
  return LEGEND_ORDER.filter((k) => set.has(k));
}

function positionStyle(pos: NonNullable<EdgeLegendProps['position']>): CSSProperties {
  switch (pos) {
    case 'bottom-left':
      return { bottom: 8, left: 8 };
    case 'top-right':
      return { top: 8, right: 8 };
    case 'top-left':
      return { top: 8, left: 8 };
    case 'bottom-right':
    default:
      return { bottom: 8, right: 8 };
  }
}

function LegendSwatch({ style }: { style: EdgeKindStyle }): JSX.Element {
  // Render the stroke style as a short inline SVG line so dash patterns
  // are reproduced exactly. width/height kept compact so the legend stays
  // out of the way in the chat-card preview.
  return (
    <svg width="28" height="10" aria-hidden="true" style={{ flexShrink: 0 }}>
      <line
        x1="0"
        y1="5"
        x2="28"
        y2="5"
        stroke={style.stroke}
        strokeWidth={style.strokeWidth}
        strokeDasharray={style.strokeDasharray}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function EdgeLegend({
  edges,
  position = 'bottom-right',
}: EdgeLegendProps): JSX.Element | null {
  const kinds = presentKinds(edges);
  if (kinds.length === 0) return null;
  return (
    <div
      data-testid="edge-legend"
      role="region"
      aria-label="Connector legend"
      style={{
        position: 'absolute',
        zIndex: 5,
        background: 'var(--tessa-bg, rgba(255,255,255,0.94))',
        border: '1px solid var(--tessa-border-soft, #E3E7ED)',
        borderRadius: 10,
        padding: '8px 11px',
        fontSize: 10,
        lineHeight: 1.2,
        color: 'var(--tessa-fg, #000)',
        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.10)',
        pointerEvents: 'none',
        ...positionStyle(position),
      }}
    >
      <div
        style={{
          fontWeight: 600,
          marginBottom: 4,
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--tessa-muted, #4C4C4C)',
        }}
      >
        Connectors
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {kinds.map((k) => {
          const style = EDGE_KIND_STYLES[k];
          return (
            <div
              key={k}
              data-edge-kind={k}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <span style={{ width: 12, textAlign: 'center' }}>{KIND_ICON[k]}</span>
              <LegendSwatch style={style} />
              <span>{style.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
